import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface MyBooking {
  id: string;
  status: string;
  createdAt: string;
  event?: { id: string; title: string; startTime: string; endTime: string; image?: string; };
  business?: { name: string; slug: string; logo?: string; city?: string; };
  service?: { name: string; price: number; };
  amount?: number;
}

const STATUS: Record<string, { label: string; color: string; bg: string }> = {
  CONFIRMED: { label: 'Confirmada', color: '#15803d', bg: '#dcfce7' },
  PENDING:   { label: 'Pendiente',  color: '#854d0e', bg: '#fef9c3' },
  CANCELLED: { label: 'Cancelada',  color: '#dc2626', bg: '#fee2e2' },
  COMPLETED: { label: 'Completada', color: '#1d4ed8', bg: '#dbeafe' },
  NO_SHOW:   { label: 'No asistí',  color: '#475569', bg: '#f1f5f9' },
};

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'Europe/Madrid' });
}
function fmtTime(d: string) {
  return new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
}

export function MyBookings() {
  const [bookings, setBookings] = useState<MyBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) { navigate('/login'); return; }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUser(payload);
    } catch {}
    loadBookings(token);
  }, []);

  const loadBookings = async (token: string) => {
    try {
      const r = await axios.get(`${API_URL}/event-bookings/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(r.data.data.bookings || []);
    } catch {
      // Si falla intenta con bookings normales
      try {
        const token2 = localStorage.getItem('accessToken');
        const r2 = await axios.get(`${API_URL}/bookings/my?limit=50`, {
          headers: { Authorization: `Bearer ${token2}` },
        });
        setBookings(r2.data.data.bookings || []);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (eventId: string) => {
    if (!confirm('¿Cancelar esta inscripción?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/event-bookings/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadBookings(token!);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Error al cancelar');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', sans-serif; background: #f1f5f9; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .my-nav { background: white; border-bottom: 1px solid #e2e8f0; padding: 0 5%; height: 60px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 50; box-shadow: 0 1px 3px rgba(0,0,0,.06); }
        .my-body { max-width: 720px; margin: 0 auto; padding: 32px 20px; animation: fadeUp .3s ease; }
        .bk-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; margin-bottom: 14px; transition: transform .2s, box-shadow .2s; }
        .bk-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,.08); }
        .bk-img { height: 120px; background: linear-gradient(135deg, #1e293b, #334155); position: relative; overflow: hidden; }
        .bk-img img { width: 100%; height: 100%; object-fit: cover; }
        .bk-body { padding: 16px 20px; }
        .bk-title { font-size: .95rem; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
        .bk-biz { font-size: .78rem; color: #3b82f6; font-weight: 600; margin-bottom: 8px; }
        .bk-meta { font-size: .78rem; color: #64748b; margin-bottom: 12px; line-height: 1.6; }
        .bk-footer { display: flex; align-items: center; justify-content: space-between; padding-top: 12px; border-top: 1px solid #f1f5f9; }
        .btn-cancel-bk { padding: 7px 16px; border-radius: 8px; border: 1px solid #fee2e2; background: #fef2f2; color: #dc2626; font-size: .78rem; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .2s; }
        .btn-cancel-bk:hover { background: #fee2e2; }
        .btn-view-bk { padding: 7px 16px; border-radius: 8px; border: none; background: #dbeafe; color: #1d4ed8; font-size: .78rem; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; text-decoration: none; }
        .empty { text-align: center; padding: 64px 24px; }
        .spinner { width: 36px; height: 36px; border: 3px solid #e2e8f0; border-top: 3px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin: 48px auto; }
      `}</style>

      {/* NAV */}
      <div className="my-nav">
        <a href="https://fitpulse.es" style={{ fontWeight: 800, fontSize: '1.1rem', color: '#3b82f6', textDecoration: 'none' }}>● FitPulse</a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {user && <span style={{ fontSize: '.82rem', color: '#64748b' }}>{user.email}</span>}
          <button onClick={handleLogout} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
            Cerrar sesión
          </button>
        </div>
      </div>

      <div className="my-body">
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>Mis inscripciones</h1>
          <p style={{ fontSize: '.85rem', color: '#64748b' }}>Aquí puedes ver todos los eventos en los que te has inscrito</p>
        </div>

        {loading ? (
          <div className="spinner"></div>
        ) : bookings.length > 0 ? (
          bookings.map(b => {
            const st = STATUS[b.status] || STATUS.PENDING;
            return (
              <div key={b.id} className="bk-card">
                {b.event?.image && (
                  <div className="bk-img"><img src={b.event.image} alt={b.event?.title} /></div>
                )}
                <div className="bk-body">
                  <div className="bk-title">{b.event?.title || b.service?.name || 'Evento'}</div>
                  {b.business && (
                    <div className="bk-biz">📍 {b.business.name}{b.business.city ? ` · ${b.business.city}` : ''}</div>
                  )}
                  <div className="bk-meta">
                    {b.event?.startTime && (
                      <>📅 {fmtDate(b.event.startTime)} · 🕐 {fmtTime(b.event.startTime)} – {fmtTime(b.event.endTime)}<br/></>
                    )}
                    {b.amount || b.service?.price ? `💶 ${b.amount || b.service?.price}€` : '🆓 Gratis'}
                  </div>
                  <div className="bk-footer">
                    <span style={{ background: st.bg, color: st.color, fontSize: '.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 100 }}>
                      {st.label}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {b.business?.slug && (
                        <a href={`/b/${b.business.slug}`} className="btn-view-bk">Ver negocio</a>
                      )}
                      {b.status === 'CONFIRMED' && b.event?.id && (
                        <button className="btn-cancel-bk" onClick={() => handleCancel(b.event!.id)}>Cancelar</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty">
            <div style={{ fontSize: 56, marginBottom: 16 }}>📅</div>
            <p style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Sin inscripciones todavía</p>
            <p style={{ fontSize: '.85rem', color: '#64748b', marginBottom: 24 }}>Explora negocios y apúntate a tus actividades favoritas</p>
            <a href="https://fitpulse.es" style={{ background: '#3b82f6', color: 'white', padding: '11px 24px', borderRadius: 10, textDecoration: 'none', fontWeight: 700, fontSize: '.88rem' }}>
              Explorar negocios →
            </a>
          </div>
        )}
      </div>
    </>
  );
}
